import React, { useState, useEffect, useMemo, useRef } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import { NotificationManager } from 'react-notifications';
import { isDeepEqual } from '@mui/x-data-grid/internals';
import useMediaQuery from '@mui/material/useMediaQuery';
import { Link, useNavigate } from "react-router-dom";
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Check from '@mui/icons-material/Check';
import CancelIcon from '@mui/icons-material/Close';
import PlusIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import EditNoteIcon from '@mui/icons-material/EditNote';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import AssessmentIcon from '@mui/icons-material/Assessment';
import EditIcon from '@mui/icons-material/Edit';
import {
  DataGrid,
  GridColDef,
  GridRowId,
  GridRowModes,
  GridSortModel,
} from '@mui/x-data-grid';
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';
import FilterListIcon from '@mui/icons-material/FilterList';
import SearchIcon from '@mui/icons-material/Search';
import BusinessIcon from '@mui/icons-material/Business';
import CategoryIcon from '@mui/icons-material/Category';
import ListAltIcon from '@mui/icons-material/ListAlt';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import FlagIcon from '@mui/icons-material/Flag';
import InputAdornment from '@mui/material/InputAdornment';
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete';
import { styled } from '@mui/material/styles';
import { SensorDTO, SensorsListItem } from '../definitions/sensor';
import UploadSensorsModal from '../components/UploadSensorsModal';
import ImportSensorsModal from '../components/ImportSensorsModal';
import GenerateReportModal from '../components/GenerateReportModal';
import InventorySummary from '../components/InventorySummary';
import useSensorStore from '../store/sensorStore';
import useClientStore from '../store/clientStore';
import useFetchSensors, { FetchSensorsParams } from '../hooks/useFetchSensors';
import useDeleteSensor from '../hooks/useDeleteSensor';
import useUserStore from '../store/userStore';
import useDashboardContextStore from '../store/dashboardContextStore';
import useFetchSensorUseCases from '../hooks/useFetchSensorUseCases';
import useFetchSensorStatuses from '../hooks/useFetchSensorStatuses'
import useFetchClients from '../hooks/useFetchClients';
import useFetchSensorModels from '../hooks/useFetchSensorModels';
import useFetchSensorBillableStatuses from '../hooks/useFetchSensorBillableStatuses';
import useEditSensor from '../hooks/useEditSensor';
import useUploadInventory, { InventoryUploadResponse } from '../hooks/useUploadInventory';
import {
  STATUSES_WITH_CUSTOMER_NEEDED,
  IPAD_MEDIA_QUERY_WIDTH,
  MOBILE_MEDIA_QUERY_WIDTH,
  STATUSES_WITH_CUSTOMER_NOT_MANDATORY,
} from '../utils/constants'
import { objectCleaner } from '../utils/helpers';
import theme from '../theme';
import { useTheme } from '@mui/material/styles';
import * as FormCommon from '../components/formCommon';

const SensorTableEditBtn = ({ id }) => {
  const navigate = useNavigate();
  const { setRowModesModel, rowModesModel, quickEditSensorId } = useDashboardContextStore();

  const onClick = (e) => {
    e.stopPropagation();
    navigate(`/edit-sensor/${id}`);
    if (quickEditSensorId) {
      setRowModesModel({
        ...rowModesModel,
        [quickEditSensorId]: { mode: GridRowModes.View, ignoreModifications: true },
      })
    };
  };

  return <EditNoteIcon sx={{ cursor: 'pointer', fontSize: 27, color: '#F27A54' }} onClick={onClick} />;
};

const SensorTableQuickEditBtn = ({ id }) => {
  const { setQuickEditSensorId, setRowModesModel, rowModesModel, quickEditSensorId } = useDashboardContextStore();
  const onClick = () => {
    setQuickEditSensorId(id);
    if (quickEditSensorId) {
      setRowModesModel({
        ...rowModesModel,
        [id]: { mode: GridRowModes.Edit, fieldToFocus: 'sensorId' },
        [quickEditSensorId]: { mode: GridRowModes.View, ignoreModifications: true },
      });
    } else {
      setRowModesModel({
        ...rowModesModel,
        [id]: { mode: GridRowModes.Edit },
      });
    }
  };

  return <EditIcon sx={{ cursor: 'pointer', fontSize: 27, color: '#F27A54' }} onClick={onClick} />;
};

const BLANK_BILLABLE_STATUS = 'None';

const withRowValue = (options: string[], field: keyof SensorsListItem, includeRowValue = true) =>
  ({ row }: { row?: SensorsListItem }) => {
    const value = row?.[field];
    return includeRowValue && typeof value === 'string' && value && !options.includes(value)
      ? [value, ...options]
      : options;
  };

const prepareQuickEditFormData = ({
  quickEditState,
  quickEditSensor,
  sensorUseCases,
  sensorModels,
  sensorBillableStatuses,
  clients,
  quickEditSensorStatus,
}) => {
  const isChanged = (field: string) => !!quickEditState
    && field in quickEditState
    && (quickEditState[field]?.value ?? '') !== (quickEditSensor?.[field] ?? '');
  const isClientChanged = isChanged('clientName');
  const client = isClientChanged
    ? clients.find(({ name }) => name === quickEditState.clientName?.value)
    : clients.find(({ id }) => id === quickEditSensor?.clientId);
  const useCaseEditValue = quickEditState?.sensorUseCase?.value;
  const siteName = quickEditState.siteName?.value;
  const siteId = !isClientChanged && !isChanged('siteName')
    ? quickEditSensor?.siteId
    : client
      ? client.sites.find(({ name }) => name === siteName)?.id
      : clients.find(({ isClient }) => !isClient).sites.find(({ name }) => name === siteName)?.id;
  const useCaseId = !isChanged('sensorUseCase')
    ? quickEditSensor?.useCaseId
    : useCaseEditValue !== 'None'
      ? sensorUseCases.find(({ description }) => description === quickEditState.sensorUseCase?.value)?.id
      : null;
  const sensorModelId = !isChanged('sensorModel')
    ? quickEditSensor?.sensorModelId
    : sensorModels.find(({ description }) => description === quickEditState.sensorModel?.value)?.id;
  const sensorBillableStatusId = !isChanged('sensorBillableStatus')
    ? quickEditSensor?.sensorBillableStatusId ?? null
    : sensorBillableStatuses
      .find(({ description }) => description === quickEditState.sensorBillableStatus?.value)?.id ?? null;
  const isSiteIdRequired = quickEditSensorStatus !== 'Sold';
  if (!siteId && isSiteIdRequired) {
    return NotificationManager.error('Location is required!');
  };
  if (quickEditSensorStatus === 'Installed' && !useCaseId) {
    return NotificationManager.error('Use case is required!');
  };
  return ({
    useCaseId: useCaseId,
    sensorModelId,
    clientId: client?.id,
    siteId,
    sensorBillableStatusId,
  });
};

const SensorActions = ({ params }) => {
  const { id, row }: { id: GridRowId, row: SensorsListItem } = params;
  const {
    quickEditSensorId,
    setQuickEditSensorId,
    setRowModesModel,
    rowModesModel,
    quickEditState,
  } = useDashboardContextStore();
  const { sensorsList, sensorModels, sensorStatuses, sensorUseCases, sensorBillableStatuses } = useSensorStore();
  const { clients } = useClientStore();
  const { editSensor } = useEditSensor();
  const theme = useTheme();

  const { deleteSensor } = useDeleteSensor();

  const [deleteId, setDeleteId] = useState<GridRowId>('');
  const [isSaving, setIsSaving] = useState(false);
  // Ref (not state) so a double click is blocked synchronously, before React re-renders.
  const savingRef = useRef(false);

  const quickEditSensor = sensorsList.find(({ id }) => id === quickEditSensorId);

  const quickEditSensorStatus = quickEditSensor?.sensorStatus;

  const onCancelClick = () => {
    setQuickEditSensorId();
    setRowModesModel({
      ...rowModesModel,
      [id]: { mode: GridRowModes.View, ignoreModifications: true },
    });
  }

  const handleSaveClick = async () => {
    if (savingRef.current) return;
    const preparedData = prepareQuickEditFormData({
      quickEditState,
      quickEditSensor,
      sensorUseCases,
      sensorModels,
      sensorBillableStatuses,
      clients,
      quickEditSensorStatus,
    });
    if (!preparedData) return;
    const data = objectCleaner({ ...quickEditSensor, ...preparedData }) as SensorDTO;
    const defaultClient = clients.find(client => !client.isClient);
    const reqData = {
      clientId: defaultClient?.id,
      ...data,
      sensorBillableStatusId: preparedData.sensorBillableStatusId,
    };
    savingRef.current = true;
    setIsSaving(true);
    try {
      const saved = await editSensor(reqData, id);
      if (!saved) return;
      setQuickEditSensorId();
      const { rowModesModel: currentRowModesModel } = useDashboardContextStore.getState();
      setRowModesModel({ ...currentRowModesModel, [id]: { mode: GridRowModes.View } });
    } finally {
      savingRef.current = false;
      setIsSaving(false);
    }
  };

  const onDeleteClick = () => {
    setDeleteId(id);
  };

  const onDeleteDialogClose = () => {
    setDeleteId('');
  };

  const handleDeleteClick = () => {
    deleteSensor(id);
    setDeleteId('');
  }

  const allowToRemove = !['Sold', 'Installed'].includes(row.sensorStatus);

  return (
    <Box sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', marginTop: '13px', gap: '8px' }}>
      <Dialog open={!!deleteId} onClose={onDeleteDialogClose}>
        <DialogTitle>Are you sure you want to delete this sensor?</DialogTitle>
        <Box sx={{ display: 'flex' }}>
          <Button sx={{ flex: 1 }} color="error" startIcon={<DeleteIcon />} onClick={handleDeleteClick}>
            Confirm
          </Button>
          <Button sx={{ flex: 1 }} color="primary" startIcon={<CancelIcon />} onClick={onDeleteDialogClose}>
            No
          </Button>
        </Box>
      </Dialog>
      {quickEditSensorId === id && isSaving ? (
        <CircularProgress size={24} sx={{ marginLeft: '2px' }} />
      ) : quickEditSensorId === id ? (
        <>
          <Check
            onClick={handleSaveClick}
            sx={{ cursor: 'pointer', fontSize: 27, color: '#F27A54' }}
          />
          <ActionSeparator />
          <CancelIcon
            onClick={onCancelClick}
            sx={{ cursor: 'pointer', fontSize: 27, color: theme.palette.primary.main }}
          />
        </>
      ) : (
        <>
          <SensorTableQuickEditBtn id={id} />
          <ActionSeparator />
          <SensorTableEditBtn id={id} />
          {allowToRemove && (
            <>
              <ActionSeparator />
              <DeleteIcon
                onClick={onDeleteClick}
                sx={{ cursor: 'pointer', fontSize: 27, color: 'red' }}
              />
            </>
          )}
        </>
      )}
    </Box>
  )
}

const PageTitle = styled('h1')({
  fontSize: 24,
  fontWeight: 700,
  color: '#1f2937',
  margin: 0,
});

const PageSubtitle = styled('p')({
  fontSize: 14,
  color: '#6b7280',
  margin: '4px 0 0',
});

const HeaderRow = styled('div')({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: 16,
  marginBottom: 15,
});

const Actions = styled('div')(() => ({
  display: 'flex',
  justifyContent: 'flex-end',
}));

const StickyHeader = styled('div')(({ theme }) => ({
  position: 'sticky',
  top: 64,
  zIndex: theme.zIndex.appBar,
  backgroundColor: '#fff',
  paddingTop: 16,
}));

const FiltersCard = styled('div')({
  border: '1px solid #eee',
  borderRadius: 8,
  backgroundColor: '#fafafa',
  padding: '12px 16px 16px',
  marginBottom: 15,
});

const FiltersTitle = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  marginBottom: 10,
  fontSize: 13,
  fontWeight: 600,
  color: '#757575',
  textTransform: 'uppercase',
  letterSpacing: 0.5,
});

const FiltersRow = styled('div')({
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'flex-start',
  gap: 12,
});

const FilterFieldWrap = styled('div')({
  flex: '1 1 160px',
  minWidth: 160,
});

const ButtonStyled = styled(Button)(() => ({
  marginRight: 10,
  backgroundColor: theme.palette.primary.main,
  '&:hover': {
    background: theme.palette.primary.dark,
  },
}));

const MutedButtonStyled = styled(Button)(() => ({
  marginRight: 10,
  backgroundColor: '#fff',
  color: '#414042',
  border: '1px solid #d1d5db',
  boxShadow: 'none',
  '&:hover': {
    backgroundColor: '#f9fafb',
    boxShadow: 'none',
  },
}));

const MutedButtonStyledOrange = styled(Button)(() => ({
  marginRight: 10,
  backgroundColor: '#fff',
  color: theme.palette.primary.main,
  border: '1px solid #d1d5db',
  boxShadow: 'none',
  '&:hover': {
    backgroundColor: '#f9fafb',
    boxShadow: 'none',
  },
}));

const ActionSeparator = styled('div')({
  width: '1px',
  backgroundColor: '#ccc', // Light gray color for the separator
  height: '24px', // Height to match the button height
});



export default function Dashboard() {
  const { user } = useUserStore();
  const { clients } = useClientStore();
  const { sensorsList, sensorsPage, sensorUseCases, sensorStatuses, sensorModels, sensorBillableStatuses } = useSensorStore();
  const { fetchSensors, loading } = useFetchSensors();
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: sensorsPage.pageSize || 25 });
  const [sortModel, setSortModel] = useState<GridSortModel>([]);
  const [customFilters, setCustomFilters] = useState({
    sensorId: '',
    clientId: '' as number | '',
    sensorModelId: '' as number | '',
    useCaseId: '' as number | '',
    siteId: '' as number | '',
    statusId: '' as number | '',
  });
  const [sensorIdInput, setSensorIdInput] = useState('');
  const [isOpenFileUploadModal, setIsOpenFileUploadModal] = useState(false);
  const [isOpenGenerateReportModal, setIsOpenGenerateReportModal] = useState(false);
  const [isOpenImportSensorsModal, setIsOpenImportSensorsModal] = useState(false);
  const [importResponse, setImportResponse] = useState<InventoryUploadResponse | null>(null);
  const importFileInputRef = useRef<HTMLInputElement>(null);
  const stickyHeaderRef = useRef<HTMLDivElement>(null);
  const gridWrapperRef = useRef<HTMLDivElement>(null);
  const [gridTopOffset, setGridTopOffset] = useState(0);

  useEffect(() => {
    const stickyEl = stickyHeaderRef.current;
    const wrapperEl = gridWrapperRef.current;
    if (!stickyEl || !wrapperEl) return;

    const updateGridTopOffset = () => {
      setGridTopOffset(wrapperEl.getBoundingClientRect().top + window.scrollY);
    };

    updateGridTopOffset();
    const observer = new ResizeObserver(updateGridTopOffset);
    observer.observe(stickyEl);
    window.addEventListener('resize', updateGridTopOffset);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', updateGridTopOffset);
    };
  }, []);
  const {
    rowModesModel,
    quickEditSensorId,
    quickEditState,
    setQuickEditState,
  } = useDashboardContextStore();
  const { fetchSensorsUseCases } = useFetchSensorUseCases();
  const { fetchSensorsStatuses } = useFetchSensorStatuses();
  const { fetchClients } = useFetchClients();
  const { fetchSensorsModels } = useFetchSensorModels();
  const { fetchSensorBillableStatuses } = useFetchSensorBillableStatuses();
  const { uploadInventory, loading: importLoading } = useUploadInventory();

  const isIpad = useMediaQuery(IPAD_MEDIA_QUERY_WIDTH);
  const isMobile = useMediaQuery(MOBILE_MEDIA_QUERY_WIDTH);

  useEffect(() => {
    if (user) {
      fetchSensorsUseCases();
      fetchSensorsStatuses();
      fetchClients();
      fetchSensorsModels();
      fetchSensorBillableStatuses();
    }
  }, [user]);

  const allSites = useMemo(() => {
    const sitesById = new Map<number, { id: number; name: string; clientName: string }>();
    clients.forEach((client) => {
      client.sites.forEach((site) => {
        sitesById.set(site.id, { id: site.id, name: site.name, clientName: client.name });
      });
    });
    return Array.from(sitesById.values());
  }, [clients]);

  useEffect(() => {
    const handle = setTimeout(() => {
      setCustomFilters((prev) => (prev.sensorId === sensorIdInput ? prev : { ...prev, sensorId: sensorIdInput }));
    }, 400);
    return () => clearTimeout(handle);
  }, [sensorIdInput]);

  const filterParams = useMemo(() => {
    const params: Partial<FetchSensorsParams> = {};
    if (customFilters.sensorId) params.sensorId = customFilters.sensorId;
    if (customFilters.clientId !== '') params.clientId = customFilters.clientId;
    if (customFilters.sensorModelId !== '') params.sensorModelId = customFilters.sensorModelId;
    if (customFilters.useCaseId !== '') params.useCaseId = customFilters.useCaseId;
    if (customFilters.siteId !== '') params.siteId = customFilters.siteId;
    if (customFilters.statusId !== '') params.statusId = customFilters.statusId;
    return params;
  }, [customFilters]);

  const filterParamsKey = JSON.stringify(filterParams);

  const hasActiveFilters = Object.values(customFilters).some((value) => value !== '');

  const activeFieldSx = (isActive: boolean, sx: object = {}) => ({
    ...sx,
    '& .MuiOutlinedInput-root': {
      backgroundColor: isActive ? '#fff' : 'transparent',
      '& fieldset': {
        borderColor: isActive ? theme.palette.primary.main : undefined,
        borderWidth: isActive ? 2 : undefined,
      },
      '&:hover fieldset': {
        borderColor: theme.palette.primary.main,
      },
      '&.Mui-focused fieldset': {
        borderColor: theme.palette.primary.main,
      },
    },
    '& .MuiInputLabel-root.Mui-focused': {
      color: theme.palette.primary.main,
    },
  });

  const clearCustomFilters = () => {
    setSensorIdInput('');
    setCustomFilters({
      sensorId: '',
      clientId: '',
      sensorModelId: '',
      useCaseId: '',
      siteId: '',
      statusId: '',
    });
  };

  const filterClientOptions = useMemo(() => clients
    .map(({ id, name }) => ({ id, description: name }))
    .sort((a, b) => {
      if (a.description.toLowerCase() === 'no client') return -1;
      if (b.description.toLowerCase() === 'no client') return 1;
      return a.description.localeCompare(b.description);
    }), [clients]);

  const filterModelOptions = useMemo(() => sensorModels
    .map(({ id, description }) => ({ id, description }))
    .sort((a, b) => a.description.localeCompare(b.description)), [sensorModels]);

  const filterUseCaseOptions = useMemo(() => sensorUseCases
    .map(({ id, description }) => ({ id, description }))
    .sort((a, b) => a.description.localeCompare(b.description)), [sensorUseCases]);

  const filterSiteOptions = useMemo(() => {
    const nameCounts = new Map<string, number>();
    allSites.forEach(({ name }) => nameCounts.set(name, (nameCounts.get(name) || 0) + 1));

    return allSites
      .map(({ id, name, clientName }) => ({
        id,
        description: (nameCounts.get(name) || 0) > 1 ? `${name} (${clientName})` : name,
      }))
      .sort((a, b) => a.description.localeCompare(b.description));
  }, [allSites]);

  const filterStatusOptions = useMemo(() => sensorStatuses
    .map(({ id, description }) => ({ id, description }))
    .sort((a, b) => a.description.localeCompare(b.description)), [sensorStatuses]);

  const autocompleteFilterOptions = createFilterOptions({ trim: true, limit: 100 }) as any;

  useEffect(() => {
    if (user) {
      fetchSensors({
        page: paginationModel.page + 1,
        sortBy: sortModel[0]?.field,
        sortDirection: sortModel[0]?.sort ?? undefined,
        ...filterParams,
      });
    }
  }, [user, paginationModel.page, sortModel, filterParamsKey]);

  useEffect(() => {
    setPaginationModel((prev) => (prev.page === 0 ? prev : { ...prev, page: 0 }));
  }, [filterParamsKey, sortModel]);

  useEffect(() => {
    if (sensorsPage.pageSize && sensorsPage.pageSize !== paginationModel.pageSize) {
      setPaginationModel((prev) => ({ ...prev, pageSize: sensorsPage.pageSize }));
    }
  }, [sensorsPage.pageSize]);

  const quickEditSensor = sensorsList.find(({ id }) => id === quickEditSensorId);

  const quickEditClientId = quickEditState?.clientName && clients.find(({ name }) => name === quickEditState.clientName.value)?.id;

  const quickEditClient = quickEditSensor && clients.find(({ id }) => id === quickEditClientId);

  const basicUseCaseEditOptions = useMemo(
    () => sensorUseCases.map(({ description }) => description).sort((a, b) => a.localeCompare(b)),
    [sensorUseCases],
  );

  const useCaseEditingOptions =
    quickEditState?.sensorStatus?.value === 'Installed'
      ? basicUseCaseEditOptions
      : ['None', ...basicUseCaseEditOptions];

  const billableStatusEditOptions = useMemo(
    () => [BLANK_BILLABLE_STATUS, ...sensorBillableStatuses.map(({ description }) => description)],
    [sensorBillableStatuses],
  );

  const sitesSelectOptions = (quickEditClient
    ? quickEditClient?.sites.map(({ name }) => name)
    : allSites.map(({ name }) => name)
  )?.sort((a, b) => a.localeCompare(b));

  const isCustomerEditable = quickEditSensor?.sensorStatus ? [
    ...STATUSES_WITH_CUSTOMER_NEEDED,
    ...STATUSES_WITH_CUSTOMER_NOT_MANDATORY,
  ].includes(quickEditSensor.sensorStatus) : false;

  // Alphabetical order, except "No Client" which is always listed first
  const clientsSelectOptions = clients
    .filter(({ isClient }) =>
      isClient ||
      (quickEditSensor?.sensorStatus && STATUSES_WITH_CUSTOMER_NOT_MANDATORY.includes(quickEditSensor.sensorStatus)))
    .map(({ name }) => name)
    .sort((a, b) => {
      if (a.toLowerCase() === 'no client') return -1;
      if (b.toLowerCase() === 'no client') return 1;
      return a.localeCompare(b);
    });

  const columns: GridColDef[] = [
    {
      field: 'sensorId',
      headerName: 'Sensor ID',
      flex: 1,
      editable: false,
      filterable: false,
      headerClassName: 'bold-header',
    },
    {
      field: 'clientName',
      headerName: 'Client',
      flex: 1,
      editable: isCustomerEditable,
      type: 'singleSelect',
      valueOptions: withRowValue(clientsSelectOptions, 'clientName'),
      filterable: false,
      headerClassName: 'bold-header',
    },
    {
      field: 'sensorModel',
      headerName: 'Model Name',
      flex: 1,
      editable: true,
      type: 'singleSelect',
      valueOptions: withRowValue(sensorModels.map(({ description }) => description).sort((a, b) => a.localeCompare(b)), 'sensorModel'),
      filterable: false,
      headerClassName: 'bold-header',
    },
    {
      field: 'sensorUseCase',
      headerName: 'Use Case',
      flex: 1,
      editable: true,
      type: 'singleSelect',
      valueOptions: withRowValue(useCaseEditingOptions, 'sensorUseCase'),
      filterable: false,
      headerClassName: 'bold-header',
    },
    {
      field: 'sensorBillableStatus',
      headerName: 'Billable Status',
      flex: 1,
      editable: true,
      type: 'singleSelect',
      valueOptions: withRowValue(billableStatusEditOptions, 'sensorBillableStatus'),
      filterable: false,
      headerClassName: 'bold-header',
    },
    {
      field: 'siteName',
      headerName: 'Location',
      flex: 1,
      editable: quickEditSensor?.sensorStatus ? quickEditSensor.sensorStatus !== 'Sold' : false,
      type: 'singleSelect',
      // The current site only belongs to the list while the client is unchanged.
      valueOptions: withRowValue(sitesSelectOptions, 'siteName', !quickEditClientId || quickEditClientId === quickEditSensor?.clientId),
      filterable: false,
      headerClassName: 'bold-header',
    },
    {
      field: 'sensorStatus',
      headerName: 'Status',
      flex: 1,
      editable: false,
      type: 'singleSelect',
      valueOptions: sensorStatuses.map(({ description }) => description).sort((a, b) => a.localeCompare(b)),
      filterable: false,
      headerClassName: 'bold-header',
    },
    {
      field: "action",
      headerName: "",
      sortable: false,
      renderCell: (params) => <SensorActions params={params} />,
      filterable: false,
      hideable: false,
      headerClassName: 'bold-header',
      width: 150,
    },
  ];

  const handleGridStateChange = (gridState) => {
    const editState = quickEditSensorId && gridState?.editRows?.[quickEditSensorId];

    if (isDeepEqual(editState, quickEditState)) return;
    setQuickEditState(editState);
  };

  const boxWidth = isMobile ? 370 : (isIpad ? 700 : '100%');

  const closeFileUploadModal = () => setIsOpenFileUploadModal(false);
  const closeGenerateReportModal = () => setIsOpenGenerateReportModal(false);
  const closeImportSensorsModal = () => {
    setIsOpenImportSensorsModal(false);
    setImportResponse(null);
  };

  const handleImportSensors = () => importFileInputRef.current?.click();

  const handleImportFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    const response = await uploadInventory(file);
    if (response) {
      setImportResponse(response);
      setIsOpenImportSensorsModal(true);
    }
  };

  return (
    <div style={{ maxWidth: boxWidth }}>
      <UploadSensorsModal
        closeFileUploadModal={closeFileUploadModal}
        isOpenFileUploadModal={isOpenFileUploadModal}
      />
      <GenerateReportModal
        isOpen={isOpenGenerateReportModal}
        onClose={closeGenerateReportModal}
      />
      <ImportSensorsModal
        open={isOpenImportSensorsModal}
        onClose={closeImportSensorsModal}
        uploadResponse={importResponse}
      />
      <StickyHeader ref={stickyHeaderRef}>
        <HeaderRow>
          <div>
            <PageTitle>Dashboard</PageTitle>
            <PageSubtitle></PageSubtitle>
          </div>
          <Actions>
            <div>
              {/* <ButtonStyled
                role={undefined}
                variant="contained"
                tabIndex={-1}
                startIcon={<AssessmentIcon />}
                onClick={() => setIsOpenGenerateReportModal(true)}
              >
                Generate Report
              </ButtonStyled> */}
            </div>
            <MutedButtonStyled
              role={undefined}
              variant="contained"
              tabIndex={-1}
              startIcon={<AssessmentIcon />}
              onClick={() => window.open('https://app.powerbi.com/links/eQ0P4X1Cu0?ctid=6727db76-4b68-4a92-956f-1b9db0c13391&pbi_source=linkShare', '_blank', 'noopener,noreferrer')}
            >
              Reports
            </MutedButtonStyled>
            <MutedButtonStyledOrange
              role={undefined}
              variant="contained"
              tabIndex={-1}
              startIcon={<CloudUploadIcon />}
              onClick={handleImportSensors}
              disabled={importLoading}
            >
              {importLoading ? 'Importing...' : 'Import Sensor File'}
            </MutedButtonStyledOrange>
            <input
              ref={importFileInputRef}
              type="file"
              accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              onChange={handleImportFileChange}
              style={{ display: 'none' }}
            />
            <Link to="/add-sensor">
              <ButtonStyled
                role={undefined}
                variant="contained"
                tabIndex={-1}
                startIcon={<PlusIcon />}
              >
                Add sensor
              </ButtonStyled>
            </Link>
            {!isMobile ? (
              <div>
                {/* <ButtonStyled
                  role={undefined}
                  variant="contained"
                  tabIndex={-1}
                  startIcon={<CloudUploadIcon />}
                  onClick={() => setIsOpenFileUploadModal(true)}
                >
                  Upload sensors
                </ButtonStyled> */}
              </div>
            ) : null}
          </Actions>
        </HeaderRow>
        <InventorySummary />
        <FiltersCard>
          <FiltersTitle>
            <FilterListIcon fontSize="small" />
            Filters
          </FiltersTitle>
          <FiltersRow>
            <FilterFieldWrap>
              <FormCommon.StyledTextField
                size="small"
                fullWidth
                label="Sensor ID"
                value={sensorIdInput}
                onChange={(event) => setSensorIdInput(event.target.value)}
                sx={activeFieldSx(!!sensorIdInput, { marginBottom: 0 })}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
                }}
              />
            </FilterFieldWrap>
            <FilterFieldWrap>
              <Autocomplete
                size="small"
                fullWidth
                options={filterClientOptions}
                getOptionLabel={(option) => option.description}
                filterOptions={autocompleteFilterOptions}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                getOptionKey={(option) => option.id}
                value={filterClientOptions.find((option) => option.id === customFilters.clientId) || null}
                onChange={(_, option: { id: number; description: string } | null) => setCustomFilters((prev) => ({ ...prev, clientId: option?.id ?? '' }))}
                renderInput={(params) => (
                  <FormCommon.StyledTextField
                    {...params}
                    label="Client"
                    sx={activeFieldSx(customFilters.clientId !== '', { marginBottom: 0 })}
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: <InputAdornment position="start"><BusinessIcon fontSize="small" /></InputAdornment>,
                    }}
                  />
                )}
              />
            </FilterFieldWrap>
            <FilterFieldWrap>
              <Autocomplete
                size="small"
                fullWidth
                options={filterModelOptions}
                getOptionLabel={(option) => option.description}
                filterOptions={autocompleteFilterOptions}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                getOptionKey={(option) => option.id}
                value={filterModelOptions.find((option) => option.id === customFilters.sensorModelId) || null}
                onChange={(_, option: { id: number; description: string } | null) => setCustomFilters((prev) => ({ ...prev, sensorModelId: option?.id ?? '' }))}
                renderInput={(params) => (
                  <FormCommon.StyledTextField
                    {...params}
                    label="Model Name"
                    sx={activeFieldSx(customFilters.sensorModelId !== '', { marginBottom: 0 })}
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: <InputAdornment position="start"><CategoryIcon fontSize="small" /></InputAdornment>,
                    }}
                  />
                )}
              />
            </FilterFieldWrap>
            <FilterFieldWrap>
              <Autocomplete
                size="small"
                fullWidth
                options={filterUseCaseOptions}
                getOptionLabel={(option) => option.description}
                filterOptions={autocompleteFilterOptions}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                getOptionKey={(option) => option.id}
                value={filterUseCaseOptions.find((option) => option.id === customFilters.useCaseId) || null}
                onChange={(_, option: { id: number; description: string } | null) => setCustomFilters((prev) => ({ ...prev, useCaseId: option?.id ?? '' }))}
                renderInput={(params) => (
                  <FormCommon.StyledTextField
                    {...params}
                    label="Use Case"
                    sx={activeFieldSx(customFilters.useCaseId !== '', { marginBottom: 0 })}
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: <InputAdornment position="start"><ListAltIcon fontSize="small" /></InputAdornment>,
                    }}
                  />
                )}
              />
            </FilterFieldWrap>
            <FilterFieldWrap>
              <Autocomplete
                size="small"
                fullWidth
                options={filterSiteOptions}
                getOptionLabel={(option) => option.description}
                filterOptions={autocompleteFilterOptions}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                getOptionKey={(option) => option.id}
                value={filterSiteOptions.find((option) => option.id === customFilters.siteId) || null}
                onChange={(_, option: { id: number; description: string } | null) => setCustomFilters((prev) => ({ ...prev, siteId: option?.id ?? '' }))}
                renderInput={(params) => (
                  <FormCommon.StyledTextField
                    {...params}
                    label="Location"
                    sx={activeFieldSx(customFilters.siteId !== '', { marginBottom: 0 })}
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: <InputAdornment position="start"><LocationOnIcon fontSize="small" /></InputAdornment>,
                    }}
                  />
                )}
              />
            </FilterFieldWrap>
            <FilterFieldWrap>
              <Autocomplete
                size="small"
                fullWidth
                options={filterStatusOptions}
                getOptionLabel={(option) => option.description}
                filterOptions={autocompleteFilterOptions}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                getOptionKey={(option) => option.id}
                value={filterStatusOptions.find((option) => option.id === customFilters.statusId) || null}
                onChange={(_, option: { id: number; description: string } | null) => setCustomFilters((prev) => ({ ...prev, statusId: option?.id ?? '' }))}
                renderInput={(params) => (
                  <FormCommon.StyledTextField
                    {...params}
                    label="Status"
                    sx={activeFieldSx(customFilters.statusId !== '', { marginBottom: 0 })}
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: <InputAdornment position="start"><FlagIcon fontSize="small" /></InputAdornment>,
                    }}
                  />
                )}
              />
            </FilterFieldWrap>
            <MutedButtonStyled
              role={undefined}
              variant="contained"
              tabIndex={-1}
              disabled={!hasActiveFilters}
              startIcon={<FilterAltOffIcon />}
              onClick={clearCustomFilters}
              sx={{ flex: '0 0 auto', alignSelf: 'flex-start', marginTop: '2px' }}
            >
              Clear Filters
            </MutedButtonStyled>
          </FiltersRow>
        </FiltersCard>
      </StickyHeader>
      <div ref={gridWrapperRef} style={{ width: '100%', overflowX: 'auto', height: `calc(100vh - ${gridTopOffset + 24}px)` }}>
        <DataGrid
          onStateChange={handleGridStateChange}
          rows={sensorsList}
          loading={loading}
          columns={columns}
          editMode="row"
          getRowId={(row: SensorsListItem) => row.id}
          rowModesModel={rowModesModel}
          sx={{
            minWidth: 700,
            height: '100%',
          }}
          paginationMode="server"
          sortingMode="server"
          rowCount={sensorsPage.totalCount}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          sortModel={sortModel}
          onSortModelChange={setSortModel}
          pageSizeOptions={sensorsPage.pageSize ? [sensorsPage.pageSize] : [paginationModel.pageSize]}
        />
      </div>
    </div>
  );
}
